import React, { useMemo } from 'react';
import { Tag, Spin, Tooltip, Popover } from 'antd';
import { useTranslation } from 'react-i18next';
import { STEPS } from '../constants';

/**
 * SelectedValuesTags Component
 * Displays selected values as read-only Tags under each step title
 * Shows "Chargement..." while fetching labels, then displays actual labels
 *
 * @param {Object} props
 * @param {number} props.step - Step number (1-4)
 * @param {Object} props.selections - All selections object (IDs only)
 * @param {Object} props.enrichedData - Enriched data with labels
 * @param {number} props.maxVisible - Maximum number of tags to show before "+N" (default: 1)
 */
const SelectedValuesTags = React.memo(
  ({ step, selections, enrichedData = {}, maxVisible = 1 }) => {
    const { t } = useTranslation('riskCalculator');

    // Get display values for the current step
    const displayValues = useMemo(() => {
      switch (step) {
        case STEPS.CULTURES: {
          const cultures = selections.cultures || [];
          if (cultures.length === 0) return [];

          // Check if we have enriched culture data
          const enrichedCultures = enrichedData.cultures || [];
          const cultureChildren = enrichedData.cultureChildren || [];
          const hasLabels = enrichedCultures.length > 0;

          // Show actual culture names if available, otherwise show loading
          if (hasLabels) {
            // Parent cultures
            const parentItems = enrichedCultures.map((culture) => ({
              key: culture.value || culture.id,
              label: culture.title || culture.label || 'Chargement...',
              isParent: culture.has_children,
            }));

            // Children of parent cultures (shown in popup as flat list)
            const childItems = cultureChildren.map((child) => ({
              key: `child-${child.id}`,
              label: child.title || child.label,
              isChild: true,
              parentName: child.parentName,
            }));

            // Combine parent cultures and their children as flat list
            return [...parentItems, ...childItems];
          }

          // Show loading tag while fetching
          return [
            {
              key: 'cultures-loading',
              label: null, // Will render spinner
              loading: true,
              count: cultures.length,
            },
          ];
        }

        case STEPS.TREATMENT_TYPE: {
          const type = selections.treatmentType;
          if (!type) return [];

          // Check if we have enriched data
          const enrichedType = enrichedData.treatmentType;
          const hasLabel = enrichedType && enrichedType.name;

          return [
            {
              key: type,
              label: hasLabel ? enrichedType.name : null, // Will render spinner if null
              loading: !hasLabel,
            },
          ];
        }

        case STEPS.APPLICATION_TECHNIQUE: {
          const technique = selections.applicationTechnique;
          if (!technique) return [];

          // Check if we have enriched data
          const enrichedTechnique = enrichedData.applicationTechnique;
          const hasLabel = enrichedTechnique && enrichedTechnique.name;

          return [
            {
              key: technique,
              label: hasLabel ? enrichedTechnique.name : null, // Will render spinner if null
              loading: !hasLabel,
            },
          ];
        }

        case STEPS.COMMERCIAL_PRODUCTS: {
          const products = selections.commercialProducts || [];
          if (products.length === 0) return [];

          // Use enrichedData.products for consistent labels (like other steps)
          const enrichedProducts = enrichedData.products || [];
          const hasLabels = enrichedProducts.length > 0;

          if (hasLabels) {
            // Map enriched products by ID for fast lookup
            const productMap = new Map(enrichedProducts.map(p => [p.id, p]));

            return products.map((product) => {
              const enriched = productMap.get(product.id);
              return {
                key: product.id,
                label: enriched ? enriched.name : null, // Show spinner if no label yet
                loading: !enriched,
              };
            });
          }

          // Show loading while fetching labels
          return [{
            key: 'products-loading',
            label: null,
            loading: true,
            count: products.length,
          }];
        }

        default:
          return [];
      }
    }, [step, selections, enrichedData, t]);

    // Don't render anything if no selections
    if (displayValues.length === 0) {
      return null;
    }

    // Show only first N tags and "+X" for the rest
    const visibleItems = displayValues.slice(0, maxVisible);
    const remainingItems = displayValues.slice(maxVisible);
    const hasMore = remainingItems.length > 0;

    const renderTag = (item) => {
      const tagContent = (
        <Tag className="max-w-[140px] overflow-hidden text-ellipsis whitespace-nowrap block">
          {item.loading ? (
            <Spin size="small" className="mr-1" />
          ) : (
            item.label
          )}
          {item.count &&
            item.loading &&
            ` ${item.count} ${item.count === 1 ? 'culture' : 'cultures'}`}
        </Tag>
      );

      // Only wrap with Tooltip if there's a label (not loading)
      if (item.loading || !item.label) {
        return tagContent;
      }

      return <Tooltip title={item.label}>{tagContent}</Tooltip>;
    };

    const popoverContent = (
      <div className="flex flex-wrap gap-1" style={{ maxWidth: '300px' }}>
        {remainingItems.map((item) => (
          <Tag
            key={item.key}
            style={item.isChild ? { marginLeft: '8px', borderStyle: 'dashed' } : {}}
          >
            {item.loading ? (
              <span>
                <Spin size="small" className="mr-1" /> {t('loading')}
              </span>
            ) : (
              <>
                {item.isChild && <span style={{ opacity: 0.7 }}>↳ </span>}
                {item.label}
              </>
            )}
          </Tag>
        ))}
      </div>
    );

    return (
      <div className="mt-2 flex items-center gap-1">
        {visibleItems.map((item) => (
          <span key={item.key}>{renderTag(item)}</span>
        ))}
        {hasMore && (
          <Popover content={popoverContent} trigger={['hover', 'click']} placement="bottom">
            <Tag className="cursor-pointer">
              +{remainingItems.length}
            </Tag>
          </Popover>
        )}
      </div>
    );
  }
);

SelectedValuesTags.displayName = 'SelectedValuesTags';

export default SelectedValuesTags;
